import React from 'react';
import { getAuth } from "firebase/auth";

// Initialize Stripe with your publishable key
// Publishable keys are safe to include in frontend code
// Use test key for development, replace with live key for production
//const STRIPE_PUBLISHABLE_KEY = 'pk_test_51S2FgrGrwEWkGjX13W21NIdEnocDYumEgrU1xs2H5FW03qWBD1Nv6BHZ3TYWRQGLy8Ci1pR39kXdvguPTSS2Avgv007AQ8TXi4';
const STRIPE_PUBLISHABLE_KEY = 'pk_live_51S2FgfGRS1gQs9fU81vRSxdANNlq21oaQfVge1aJbsWfbzKmEGKwzww4jDA0m5hGT7nBkpJhKYAJoqtkYvZZMiwE00gSQrIkw9';
// Try env variable first, fallback to hardcoded key
const stripeKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || STRIPE_PUBLISHABLE_KEY;
const isValidKey = stripeKey && stripeKey.startsWith('pk_');

// We don't need to load Stripe.js since we're using server-side checkout sessions
// Just log configuration status
if (isValidKey) {
  console.log('✅ Stripe configured with:', stripeKey.substring(0, 20) + '...');
} else {
  console.warn('⚠️ Stripe is not configured properly');
}

/**
 * Create a Stripe Checkout Session for purchasing items
 * and redirect directly to the session URL (no Stripe.js).
 */

export const createCheckoutSession = async (items, customerEmail, successUrl, cancelUrl, dietaryNotes = '') => {
  try {
    // 1) Auth (your HTTP function requires an ID token)
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) throw new Error("User not authenticated");
    const idToken = await user.getIdToken(true); // force fresh token

    // 2) Choose the right function URL
    const isDev =
      window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1";
    const functionUrl = isDev
      ? "http://127.0.0.1:5001/idea-farm-382512/us-central1/createStripeCheckoutSessionHTTP"
      : "https://createstripecheckoutsessionhttp-ulvsvtfh4q-uc.a.run.app";

    // 3) Create the session (flat body is fine; your backend supports it)
    const resp = await fetch(functionUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify({
        items,
        customerEmail, // optional; Checkout also collects email
        successUrl: successUrl || `${window.location.origin}/order-success`,
        cancelUrl: cancelUrl || `${window.location.origin}/cart`,
        dietaryNotes: dietaryNotes || '',
      }),
    });

    const payload = await resp.json();
    console.log("Backend response:", payload); // Debug log
    
    if (!resp.ok) {
      console.error("Function response error:", payload);
      throw new Error(payload.message || "Failed to create checkout session");
    }

    const { result } = payload;
    console.log("Session result:", result); // Debug log
    
    if (!result || !result.url) {
      console.error("Invalid result structure:", result);
      throw new Error("No checkout URL from server");
    }

    console.log("Redirecting to:", result.url); // Debug log
    
    // 4) ⬇️ THIS is the key change: skip Stripe.js, go straight to the session URL
    window.location.assign(result.url);

    return result;
  } catch (err) {
    console.error("Error creating checkout session:", err);
    throw err;
  }
};


/**
 * Create a guest Stripe Checkout Session (no auth required).
 * Stripe collects email and shipping address.
 */
export const createGuestCheckoutSession = async (items, successUrl, cancelUrl, dietaryNotes = '') => {
  try {
    const isDev =
      window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1";
    // After deploy, check Firebase console for the actual URL and update if needed
    const functionUrl = isDev
      ? "http://127.0.0.1:5001/idea-farm-382512/us-central1/createGuestCheckoutSessionHTTP"
      : "https://createguestcheckoutsessionhttp-ulvsvtfh4q-uc.a.run.app";

    const resp = await fetch(functionUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        items,
        successUrl: successUrl || `${window.location.origin}/order-success`,
        cancelUrl: cancelUrl || `${window.location.origin}/cart`,
        dietaryNotes: dietaryNotes || '',
      }),
    });

    const payload = await resp.json();
    console.log("Guest checkout response:", payload);

    if (!resp.ok) {
      console.error("Guest checkout error:", payload);
      throw new Error(payload.message || "Failed to create checkout session");
    }

    const { result } = payload;
    if (!result || !result.url) {
      throw new Error("No checkout URL from server");
    }

    window.location.assign(result.url);
    return result;
  } catch (err) {
    console.error("Error creating guest checkout session:", err);
    throw err;
  }
};

/**
 * Format price for Stripe (converts to smallest currency unit)
 * For SEK, multiply by 100 (1 SEK = 100 öre)
 */
export const formatPriceForStripe = (price) => {
  return Math.round(price * 100);
};

/**
 * Test card numbers for development
 */
export const TEST_CARDS = {
  success: '4242 4242 4242 4242',
  decline: '4000 0000 0000 0002',
  authentication: '4000 0025 0000 3155'
};

/**
 * Check if we're in test mode
 */
export const isTestMode = () => {
  const key = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || STRIPE_PUBLISHABLE_KEY;
  return key && key.startsWith('pk_test_');
};

/**
 * Check if Stripe is configured
 */
export const isStripeConfigured = () => {
  const key = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || STRIPE_PUBLISHABLE_KEY;
  return key && (key.startsWith('pk_test_') || key.startsWith('pk_live_'));
};

/**
 * Display test mode banner
 */
export const TestModeBanner = () => {
  if (!isTestMode()) return null;
  
  return (
    <div style={{
      background: '#ffeaa7',
      padding: '10px',
      textAlign: 'center',
      borderBottom: '2px solid #fdcb6e',
      fontSize: '14px',
      fontWeight: 'bold'
    }}>
      🧪 STRIPE TEST MODE - Use card {TEST_CARDS.success} for testing
    </div>
  );
};

// We don't export stripePromise anymore since we're using server-side checkout
export default null;
